<template>
    <!-- Large Nav -->
    <nav class="navbar navbar-expand-lg bg-body-tertiary" v-if="width > 992">
        <div class="container-fluid px-0 align-items-center justify-content-between">
            <!-- Site Name -->
            <p class="w-25 fw-semi-bold fs-4 m-0 navbar-brand">ExploreMeds</p>
            <!-- Survey Title -->
            <p class="m-0 fs-4 fw-medium w-50 text-center">Survey</p>
            <!-- Close and Exit (Survey page) -->
            <div class="w-25 d-flex justify-content-end">
                <button type="button" class="btn btn-white border-0 rounded-pill bg-hover-gray lg-sv-btn" v-ripple
                    data-bs-toggle="modal" data-bs-target="#saveModal">Exit</button>
            </div>
        </div>
    </nav>

    <!-- Small Nav -->
    <nav class="navbar small-nav p-0" v-else>
        <div class="container-fluid justify-content-between p-0 flex-nowrap">
            <!-- Back button (Survey Mode) -->
            <i class="hoverable-icon bi-arrow-left" v-ripple @click="goBack"></i>

            <!-- Survey Title -->
            <p class="m-0 fs-5 fw-medium line-break-any" v-max-lines="1">Survey</p>

            <!-- Close button (Survey Mode) -->
            <i class="hoverable-icon bi-x" v-ripple data-bs-toggle="modal" data-bs-target="#saveModal"></i>
        </div>
    </nav>

    <!-- Progress Bar -->
    <div class="progress thin">
        <div :style="'width: 20%;'" class="progress-bar bg-secondary" role="progressbar" aria-label="Survey Progress"
            :aria-valuenow="20" aria-valuemin="0" aria-valuemax="100">
        </div>
    </div>

    <div class="row">
        <!-- Main container -->
        <div class="w-100 w-md-50 mw-500 mx-auto lh-md" :class="width < 500 ? 'overflow-hidden' : ''">
            <Transition :name="transitionName" mode="out-in">
                <component :is="getComponentType()" :key="currentPageId" @page-viewed="handlepageViewed">
                </component>
            </Transition>
        </div>

        <div class="w-100 w-md-50 mw-500 mx-auto fixed-bottom pt-2 pb-4 pb-lg-2r d-flex gap-3 bg-white"
            v-if="currentPageId == 0">
            <button class="btn btn-dark rounded-pill py-7 w-50" data-bs-toggle="modal"
                data-bs-target="#saveModal">Decline</button>
            <button class="btn btn-dark rounded-pill py-7 w-50" @click="goForward"
                :disabled="!pageViewed">Accept</button>
        </div>

        <div class="w-100 w-md-50 mw-500 mx-auto fixed-bottom pt-2 pb-4 pb-lg-2r d-flex gap-3 bg-white"
            v-if="currentPageId == 1">
            <button class="btn btn-dark rounded-pill py-7 w-100" @click="goForward" :disabled="!pageViewed">Start the
                Survey</button>
        </div>
    </div>

    <!-- Exit Modal -->
    <div class="modal fade" tabindex="-1" id="saveModal" aria-labelledby="saveModalLabel" aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered sheetModal popOutModal">
            <div class="modal-content rounded-sm">
                <div class="modal-body d-flex flex-column py-4 align-items-center">
                    <p class="fs-4 fw-semibold text-center mb-2">Exit</p>
                    <p class="text-center mb-2 text-secondary mb-4">Are you sure you want to exit?</p>
                    <p class="fs-5 fw-semibold text-center mb-2">Your data will not be saved</p>
                    <button class="btn btn-dark rounded-pill w-100 py-7 mb-sm-3" v-load-page="'/'" v-ripple
                        data-bs-dismiss="modal">Confirm</button>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { onMounted, onUnmounted, ref } from 'vue';
import { useWindowSize } from '@vueuse/core'
import TermsContent from '@/views/TermsPages/Content.vue';
import TermsConfirmation from '@/views/TermsPages/Confirmation.vue';
import { useRouter } from "vue-router";

const { width } = useWindowSize();
const currentPageId = ref(0);
const transitionName = ref('');
const pageViewed = ref(false);
const router = useRouter();

onMounted(() => {
    let modalEl = document.querySelector('#saveModal');
    modalEl.addEventListener('hide.bs.modal', () => {
        modalEl.querySelector('.modal-dialog').classList.add('closing');
        setTimeout(() => {
            modalEl.querySelector('.modal-dialog').classList.remove('closing');
        }, 500)
    })

    document.documentElement.classList.add('hide-scroll');
});

onUnmounted(() => {
    document.documentElement.classList.remove('hide-scroll');
})

const handlepageViewed = (val) => {
    pageViewed.value = val;
}

const getComponentType = () => {
    const questionType = pages[currentPageId.value].type;
    const componentMapping = {
        TermsContent: TermsContent,
        TermsConfirmation: TermsConfirmation,
    };

    return componentMapping[questionType] || null;
};

const goBack = () => {
    if (currentPageId.value > 0) {
        currentPageId.value--;
        transitionName.value = 'slide-prev';
        pageViewed.value = false;
    }
};

const goForward = () => {
    if (currentPageId.value < pages.length - 1) {
        currentPageId.value++;
        transitionName.value = 'slide-continue';
        pageViewed.value = false;
    } else {
        router.push('/survey')
    }
};

const pages = [
    {
        type: 'TermsContent'
    },
    {
        type: 'TermsConfirmation'
    },
]
</script>

<style scoped lang="scss">
.mw-500 {
    max-width: 500px;
}

.pb-lg {
    padding-bottom: 6em;
}

.lh-md{
    line-height: 1.6;
}

button {
    &:disabled {
        cursor: not-allowed;
        pointer-events: all !important;
    }
}

.fixed-bottom {
    padding-right: calc(var(--bs-gutter-x) * 0.5);
    padding-left: calc(var(--bs-gutter-x) * 0.5);
}

.lg-sv-btn {
    height: 2.8em;
    width: 9em;
}

.bg-hover-gray {
	&:hover {
		background-color: #f5f5f7;
	}
}

@media (min-width: 992px) {
	.pb-lg-2r {
		padding-bottom: 2.5rem !important;
	}
}

.slide-continue-enter-active,
.slide-continue-leave-active,
.slide-prev-enter-active,
.slide-prev-leave-active {
	will-change: transform;
	transform: translateZ(0);
	transition: transform 0.45s ease-in-out;
	transform-style: preserve-3d;
}

.slide-continue-leave-to {
	transform: translateX(calc((100vw * -1) - 50px));
}

.slide-continue-enter-from {
	transform: translateX(100vw);
}

.slide-continue-enter-to {
	transform: translateX(0%);
}

.slide-prev-leave-to {
	transform: translateX(100vw);
}

.slide-prev-enter-from {
	transform: translateX(calc((100vw * -1) - 50px));
}

.slide-prev-enter-to {
	transform: translateX(0%);
}
</style>